import { Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import ReMark from "../../components/brand/ReMark";
import SupportMailForm from "../../components/support/SupportMailForm";
import { LegalCrossLinks, LegalMeta, LegalSection } from "../../components/legal/LegalSections";
import { toTitleCase } from "../../js/utils/titleCase";
import { REPTC_ABOUT } from "../../js/utils/reptc";
import {
  BACKUP_INSTRUCTIONS,
  DATA_RETENTION_DUTY,
  DRE_DISCLAIMER,
  EULA_SECTIONS,
  LEGAL_LAST_UPDATED,
  PLATFORM_DISCLAIMER,
  RESTORE_LIMITATIONS,
  SUPPORTED_SYSTEMS,
  SUSPENSION_LIMITATIONS,
  TERMS_BILLING,
  WARRANTY_DISCLAIMER,
} from "../../js/legal/copy";

export function LegalShell({ title, lead, children, updated = LEGAL_LAST_UPDATED }) {
  return (
    <AppLayout>
      <article className="legal-page card">
        <h1>{toTitleCase(title)}</h1>
        <div className="title-rule" />
        <LegalMeta updated={updated} />
        {lead ? <p className="page-lead">{lead}</p> : null}
        {children}
      </article>
    </AppLayout>
  );
}

export default function AboutPage() {
  return (
    <LegalShell
      title="About REChecklist"
      lead="A client-first companion for buyers and sellers — not a listing marketplace."
    >
      <p>
        <ReMark /> exists so you can name what you want with clarity, keep it organized, and share
        it with your agent when you are ready. Your agent uses <ReMark /> to keep your journey
        organized. Your office uses <ReMark /> to ensure consistent, compliant service.
      </p>
      <p>{PLATFORM_DISCLAIMER}</p>
      <p>{REPTC_ABOUT}</p>
      <p className="legal-inline-disclaimer">{DRE_DISCLAIMER}</p>
      <LegalCrossLinks />
    </LegalShell>
  );
}

export function ContactPage() {
  return (
    <LegalShell title="Contact" lead="Questions about your journey, or a note for our team.">
      <p>
        <ReMark /> —{" "}
        <a href="https://rechecklist.pro" target="_blank" rel="noreferrer">
          rechecklist.pro
        </a>
      </p>
      <p>
        DreamHome Journey —{" "}
        <a href="https://dreamhomejourney.online" target="_blank" rel="noreferrer">
          dreamhomejourney.online
        </a>
      </p>
      <SupportMailForm lead="Tell us how we can walk with you. Your note is routed to the right desk." />
      <p className="muted">
        Signed-in Support in the footer carries your role with the request.{" "}
        <a href="mailto:marketing@rechecklist.pro">marketing@rechecklist.pro</a> remains for
        partnership notes.
      </p>
      <LegalCrossLinks />
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell
      title="Privacy"
      lead="Your data belongs to you. We do not sell or share it for advertising lists."
    >
      <p>
        Checklists, portfolios, messages, and calendar events in this build live in your browser’s
        local storage on this device. Minimal server-side retention applies only where a hosted
        Accounts or messaging service is connected.
      </p>
      <p>
        Client checklists are for internal servicing of your real estate transaction — not for sale
        or redistribution. Information you provide is shared only as needed to service your
        transaction agreement(s) with your broker, unless you specifically authorize otherwise.
      </p>
      <LegalSection title={SUPPORTED_SYSTEMS.title} paragraphs={SUPPORTED_SYSTEMS.body} />
      <LegalSection title={DATA_RETENTION_DUTY.title} paragraphs={DATA_RETENTION_DUTY.body} />
      <LegalSection title={BACKUP_INSTRUCTIONS.title} steps={BACKUP_INSTRUCTIONS.steps} />
      <LegalCrossLinks />
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell
      title="Terms of use"
      lead="Use REChecklist as a clear, Fair Housing–aligned companion for your home journey."
    >
      <p>
        You remain responsible for your own advertising, disclosures, and files. Do not use this
        product to steer, screen, or describe people instead of property.
      </p>
      <p>{PLATFORM_DISCLAIMER}</p>
      <LegalSection title={TERMS_BILLING.title} paragraphs={TERMS_BILLING.paragraphs} />
      <LegalSection title={RESTORE_LIMITATIONS.title} paragraphs={RESTORE_LIMITATIONS.body} />
      <LegalSection title={SUSPENSION_LIMITATIONS.title} paragraphs={SUSPENSION_LIMITATIONS.body} />
      <p className="muted">
        The full End User License Agreement lives at <Link to="/eula">/eula</Link>. Liability and warranty
        terms live at <Link to="/disclaimer">/disclaimer</Link>.
      </p>
      <LegalCrossLinks />
    </LegalShell>
  );
}

export function EulaPage() {
  return (
    <LegalShell
      title="End User License Agreement"
      lead="By creating or using an account, you agree to this EULA and the related policies linked below."
    >
      <nav className="legal-toc" aria-label="EULA sections">
        <strong>Contents</strong>
        <ol>
          {EULA_SECTIONS.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`}>{section.title}</a>
            </li>
          ))}
        </ol>
      </nav>
      {EULA_SECTIONS.map((section) => (
        <LegalSection
          key={section.id}
          id={section.id}
          title={section.title}
          paragraphs={section.paragraphs}
        />
      ))}
      <LegalCrossLinks />
    </LegalShell>
  );
}

export function DisclaimerPage() {
  return (
    <LegalShell
      title="Disclaimer"
      lead="Please read before relying on exports, restore points, or desk tools for compliance decisions."
    >
      <p>{PLATFORM_DISCLAIMER}</p>
      <p>{DRE_DISCLAIMER}</p>
      <LegalSection title={WARRANTY_DISCLAIMER.title} paragraphs={WARRANTY_DISCLAIMER.body} />
      <LegalSection title={RESTORE_LIMITATIONS.title} paragraphs={RESTORE_LIMITATIONS.body} />
      <LegalSection title={SUSPENSION_LIMITATIONS.title} paragraphs={SUSPENSION_LIMITATIONS.body} />
      <LegalSection
        title="Not legal or compliance advice"
        paragraphs={[
          "Checklist summaries, message exports, and PDF reports are workflow records for your office — not filings with any regulator.",
          "Brokers and agents remain responsible for their DRE duties, brokerage policies, and client disclosures.",
        ]}
      />
      <LegalCrossLinks />
    </LegalShell>
  );
}
